/**
 * RebuildVsRenovate - port from `1116/omega-club/dashboard/src/components/RebuildVsRenovate.jsx`.
 * Reads from `useTenantData(slug).schemeComparisons`.
 *
 * Per CHAP-87 (Phase 1C #53).
 *
 * Parity notes vs omega-club:
 * - omega-club's hardcoded `schemeComparison` has `costPerBed`, `costPerSF`,
 *   `parking`, `constructionTime`, `year25ValueNote` fields that the
 *   chapter-build `SchemeComparison` shape lacks. costPerBed + costPerSF
 *   are derived in-component (cost / beds, cost / sqft). The other three
 *   render "—" until schema extension lands (separate ticket).
 * - The closing "Bottom Line" + "Survey 34%" hero text are tenant-specific
 *   hardcoded strings. They're retained for omega-zeta and queued for
 *   tenant_config fields (Phase 2 candidate).
 */
import { useParams } from 'react-router-dom';
import { useTenantData } from '../hooks/useTenantData.js';
import { TenantLoading, TenantError } from '../components/TenantDataStates.js';
import { formatCurrency, formatNumber } from '../lib/formatters.js';
import type { SchemeComparison } from '../../shared/types.js';

function CheckIcon() {
  return (
    <svg className="w-4 h-4 text-positive flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="w-4 h-4 text-orange flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

interface CardStyle {
  border: string;
  bg: string;
  headerBg: string;
  headerText: string;
}

// Indexed by position: 0=lowest-cost option, 1=middle, last=recommended.
const CARD_STYLES: CardStyle[] = [
  { border: 'border-gray-200', bg: 'bg-gray-50/50', headerBg: 'bg-gray-100',  headerText: 'text-gray-600' },
  { border: 'border-gray-200', bg: 'bg-white',      headerBg: 'bg-navy/5',    headerText: 'text-navy' },
  { border: 'border-orange',   bg: 'bg-white',      headerBg: 'bg-orange/5',  headerText: 'text-navy' },
];

interface Metric {
  label: string;
  read: (s: SchemeComparison) => string;
}

export default function RebuildVsRenovate() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error, refetch } = useTenantData(slug ?? '');

  if (isLoading) return <TenantLoading />;
  if (error) return <TenantError message={String(error)} onRetry={() => refetch()} />;
  if (!data) return <PageState label="No data" />;

  const schemes = [...data.schemeComparisons]; // copy; we may reorder
  if (schemes.length === 0) {
    return <PageState label="No scheme comparisons seeded for this tenant." />;
  }

  // Order: lowest-cost first, recommended (or last) at the end.
  schemes.sort((a, b) => (a.cost ?? 0) - (b.cost ?? 0));
  const cheapest = schemes[0];
  const recommended = schemes.find((s) => s.isRecommended) ?? schemes[schemes.length - 1]!;
  // CHAP-432: one decimal, not Math.round - the same correction CHAP-405 made to
  // omega-club/dashboard/src/components/RebuildVsRenovate.jsx, which was never
  // applied to this copy. 107,748,979 / 7,500,000 = 14.3665, so Math.round
  // rendered "14x" here while the board dashboard rendered "14.4x" from the same
  // two figures. Two numbers for one quantity across two surfaces is exactly the
  // defect class the figure gate exists to close.
  const valueLift =
    recommended.year25Value && cheapest && cheapest.year25Value
      ? (recommended.year25Value / cheapest.year25Value).toFixed(1)
      : null;

  const metrics: Metric[] = [
    { label: 'Total Cost',         read: (s) => formatCurrency(s.cost) },
    { label: '25-Year Value',      read: (s) => formatCurrency(s.year25Value) },
    { label: 'Square Feet',        read: (s) => (s.sqft == null ? 'n/a' : formatNumber(s.sqft)) },
    { label: 'Beds',               read: (s) => (s.beds == null ? 'n/a' : String(s.beds)) },
    { label: 'Cost / Bed',         read: (s) => (s.cost && s.beds ? formatCurrency(s.cost / s.beds) : 'n/a') },
    { label: 'Cost / SF',          read: (s) => (s.cost && s.sqft ? `$${Math.round(s.cost / s.sqft)}` : 'n/a') },
    { label: 'Annual Revenue',     read: (s) => formatCurrency(s.revenue) },
    { label: 'NOI',                read: (s) => formatCurrency(s.noi) },
  ];

  return (
    <div className="space-y-4 sm:space-y-5 min-w-0">
      {/* Header with hero callout */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 min-w-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-navy text-xl sm:text-2xl font-bold">Rebuild vs Renovate</h2>
            <p className="text-neutral text-sm sm:text-base mt-1">
              Comparing the {schemes.length} paths forward for {data.tenant.displayName}
            </p>
          </div>
          {valueLift && cheapest.year25Value && recommended.year25Value && (
            <div className="flex flex-col items-end flex-shrink-0">
              <span className="text-xs uppercase tracking-wide text-neutral font-bold">25-yr value lift</span>
              <span className="text-orange text-2xl sm:text-3xl font-bold">{valueLift}×</span>
              <span className="text-xs text-neutral">
                {formatCurrency(cheapest.year25Value)} → {formatCurrency(recommended.year25Value)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Scheme Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
        {schemes.map((scheme, i) => {
          const isRecommended = scheme.id === recommended.id;
          const style = CARD_STYLES[Math.min(i, CARD_STYLES.length - 1)]!;
          const costPerBed = scheme.cost && scheme.beds ? scheme.cost / scheme.beds : null;
          return (
            <div
              key={scheme.id}
              className={`rounded-xl border-2 ${style.border} ${style.bg} overflow-hidden flex flex-col min-w-0 ${
                isRecommended ? 'shadow-lg ring-1 ring-orange/20' : 'shadow-sm'
              }`}
            >
              {/* Header */}
              <div className={`${style.headerBg} px-4 sm:px-5 py-3 sm:py-4`}>
                <div className="flex items-start justify-between gap-2 min-w-0">
                  <div className="min-w-0">
                    <h3 className={`${style.headerText} font-bold text-base sm:text-lg`}>{scheme.name}</h3>
                    {scheme.advocate && <p className="text-neutral text-xs mt-0.5">{scheme.advocate}</p>}
                  </div>
                  {/* CHAP-432: "Recommended" reads as still-being-urged. The
                      board resolved this on 5 August 2026, ten votes to two. */}
                  {isRecommended && (
                    <span className="bg-orange text-white text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0 whitespace-nowrap">
                      Board approved
                    </span>
                  )}
                </div>
                {scheme.description && (
                  <p className="text-neutral text-xs sm:text-sm mt-2 leading-relaxed">{scheme.description}</p>
                )}
              </div>

              {/* 25-Year Value headline */}
              <div className={`px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 ${isRecommended ? 'bg-orange/5' : 'bg-gray-50/40'}`}>
                <p className="text-neutral text-xs uppercase tracking-wider">25-Year Value</p>
                <p className={`text-2xl font-bold ${isRecommended ? 'text-orange' : 'text-navy'}`}>
                  {formatCurrency(scheme.year25Value)}
                </p>
              </div>

              {/* Key Metrics */}
              <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                  <MetricCell label="Cost"          value={formatCurrency(scheme.cost)} />
                  <MetricCell label="Beds"          value={scheme.beds == null ? 'n/a' : String(scheme.beds)} />
                  <MetricCell label="Square Feet"   value={scheme.sqft == null ? 'n/a' : formatNumber(scheme.sqft)} />
                  <MetricCell label="Cost / Bed"    value={costPerBed == null ? 'n/a' : formatCurrency(costPerBed)} />
                  <MetricCell label="Revenue"       value={scheme.revenue && scheme.revenue > 0 ? formatCurrency(scheme.revenue) : '$0'} positive={!!scheme.revenue && scheme.revenue > 0} />
                  <MetricCell label="NOI"           value={scheme.noi && scheme.noi > 0 ? formatCurrency(scheme.noi) : '$0'} positive={!!scheme.noi && scheme.noi > 0} />
                </div>
              </div>

              {/* Pros */}
              <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 flex-1">
                <p className="text-navy text-xs font-semibold uppercase tracking-wider mb-2">Advantages</p>
                <ul className="space-y-1.5">
                  {scheme.pros.map((pro, j) => (
                    <li key={j} className="flex items-start gap-2 text-xs sm:text-sm text-navy">
                      <CheckIcon />
                      <span>{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Cons */}
              <div className="px-4 sm:px-5 py-3 sm:py-4">
                <p className="text-navy text-xs font-semibold uppercase tracking-wider mb-2">Tradeoffs</p>
                <ul className="space-y-1.5">
                  {scheme.cons.map((con, j) => (
                    <li key={j} className="flex items-start gap-2 text-xs sm:text-sm text-neutral">
                      <XIcon />
                      <span>{con}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      {/* Side-by-Side Comparison Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100">
          <h3 className="text-navy font-bold text-base sm:text-lg">Side-by-Side Comparison</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-neutral text-xs font-medium uppercase tracking-wider px-4 sm:px-6 py-3">Metric</th>
                {schemes.map((scheme) => (
                  <th
                    key={scheme.id}
                    className={`text-left text-xs font-medium uppercase tracking-wider px-4 sm:px-6 py-3 ${
                      scheme.id === recommended.id ? 'text-orange' : 'text-navy'
                    }`}
                  >
                    {scheme.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {metrics.map((metric, j) => (
                <tr key={metric.label} className={j % 2 === 0 ? 'bg-gray-50/50' : ''}>
                  <td className="px-4 sm:px-6 py-2.5 text-neutral text-xs sm:text-sm font-medium">{metric.label}</td>
                  {schemes.map((scheme) => (
                    <td
                      key={scheme.id}
                      className={`px-4 sm:px-6 py-2.5 text-xs sm:text-sm font-semibold ${
                        scheme.id === recommended.id ? 'text-navy' : 'text-navy/70'
                      }`}
                    >
                      {metric.read(scheme)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MetricCell({ label, value, positive }: { label: string; value: string; positive?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="text-neutral text-xs uppercase tracking-wider">{label}</p>
      <p className={`font-bold text-sm sm:text-base truncate ${positive ? 'text-positive' : 'text-navy'}`}>{value}</p>
    </div>
  );
}

function PageState({ label, detail, tone }: { label: string; detail?: string; tone?: 'error' }) {
  const color = tone === 'error' ? 'text-red-700' : 'text-neutral';
  return (
    <div className="p-8 text-center">
      <p className={`text-sm ${color}`}>{label}</p>
      {detail && <pre className="text-xs text-gray-500 mt-2 whitespace-pre-wrap">{detail}</pre>}
    </div>
  );
}
